import { z } from "zod";

export const EVAL_RUN_SPEC_VERSION = 1;
export const MAX_EVAL_ATTEMPTS_PER_RUN = 200;

const safeIdentifierSchema = z
  .string()
  .min(1)
  .max(160)
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9._:/-]*$/, "contains unsupported characters");
const modelIdentifierSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9._:/-]*$/, "contains unsupported characters");
const organizationSchema = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9][a-z0-9-]*$/, "must be a lowercase organization slug");
const runNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(/^[^\u0000-\u001f\u007f]+$/, "must not contain control characters");

export const evalAgentVariantSchema = z.enum(["cascaded", "integration", "realtime", "slow-thinker"]);
export const evalExecutionModeSchema = z.enum(["text", "audio"]);
export const evalScenarioIdSchema = safeIdentifierSchema;

export type EvalAgentVariantName = z.infer<typeof evalAgentVariantSchema>;
export type EvalExecutionMode = z.infer<typeof evalExecutionModeSchema>;

const textExecutionSchema = z
  .object({
    mode: z.literal("text"),
    scenarioConcurrency: z.number().int().min(1).max(20).default(1),
  })
  .strict();

const audioExecutionSchema = z
  .object({
    mode: z.literal("audio"),
    scenarioConcurrency: z.number().int().min(1).max(10).default(1),
    captureAudio: z.boolean().default(true),
    callerTts: z
      .object({
        model: modelIdentifierSchema,
        voice: safeIdentifierSchema.optional(),
      })
      .strict(),
    agentSttModel: modelIdentifierSchema,
    agentTtsModel: modelIdentifierSchema,
    interruptionsEnabled: z.boolean().default(true),
  })
  .strict();

export const evalRunSpecSchema = z
  .object({
    version: z.literal(EVAL_RUN_SPEC_VERSION),
    runId: z.string().uuid(),
    name: runNameSchema,
    requestedBy: z
      .object({
        userId: safeIdentifierSchema,
        email: z.string().email().optional(),
      })
      .strict(),
    source: z
      .object({
        gitSha: z.string().regex(/^[a-f0-9]{40}$/i, "must be a full git SHA"),
        imageDigest: z
          .string()
          .regex(/^sha256:[a-f0-9]{64}$/i)
          .optional(),
      })
      .strict(),
    suite: z.enum(["scenarios", "integration"]),
    organization: organizationSchema.optional(),
    scenarioIds: z.array(evalScenarioIdSchema).min(1).max(500),
    attempts: z.number().int().min(1).max(100),
    agentVariant: evalAgentVariantSchema,
    models: z
      .object({
        agent: modelIdentifierSchema,
        provider: safeIdentifierSchema.optional(),
        judge: modelIdentifierSchema,
      })
      .strict(),
    quality: z.boolean().default(false),
    execution: z.discriminatedUnion("mode", [textExecutionSchema, audioExecutionSchema]),
  })
  .strict()
  .superRefine((spec, ctx) => {
    if (new Set(spec.scenarioIds).size !== spec.scenarioIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "scenarioIds must not contain duplicates",
        path: ["scenarioIds"],
      });
    }

    if (spec.suite === "integration" && spec.attempts !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "integration runs must use exactly one attempt",
        path: ["attempts"],
      });
    }

    if (spec.suite === "integration" && spec.execution.mode !== "text") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "integration runs currently support text execution only",
        path: ["execution", "mode"],
      });
    }

    const totalAttempts = spec.scenarioIds.length * spec.attempts;

    if (totalAttempts > MAX_EVAL_ATTEMPTS_PER_RUN) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `scenarioIds * attempts must not exceed ${MAX_EVAL_ATTEMPTS_PER_RUN} total attempts per run`,
        path: ["attempts"],
      });
    }
  });

export type EvalRunSpec = z.infer<typeof evalRunSpecSchema>;

/** Validates untrusted run input before it reaches the eval process or environment. */
export function parseEvalRunSpec(input: unknown): EvalRunSpec {
  return evalRunSpecSchema.parse(input);
}
